import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { PrismicToolbar } from '@cobliteam/prismic';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import React, { Suspense, useEffect, useState } from 'react';
import { prismicSvg, storybookSvg, tailwindSvg } from './assets';
import BorderBeam from './components/BorderBeam';
import DeveloperToolsDialog from './components/DeveloperToolsDialog';
import PositionData from './components/PositionData';
import TailwindIndicator from './components/TailwindIndicator';

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'bottom-center' | 'top-center';

const DISABLED_DEV_TOOLS = false;

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

function DeveloperTools() {
  const { developerTools } = useFeatureFlags();
  const [isOpen, setIsOpen] = useState(false);
  const [showTailwindIndicator, setShowTailwindIndicator] = useState(false);
  const [showPrismicToolbar, setShowPrismicToolbar] = useState(false);
  const [showDeveloperToolsDialog, setShowDeveloperToolsDialog] = useState(false);
  const [position, setPosition] = useState<Position>('top-center');
  const isLocalhost = typeof window !== 'undefined' && window.location.hostname.includes('localhost');
  const isPreview = typeof window !== 'undefined' && window.location.hostname.includes('preview');
  const isDev = isLocalhost || isPreview;

  const user = useUser().getUser();
  const isCobler = user.role === 'cobler';
  const isTest = process.env.NODE_ENV === 'test';

  const handleSetPosition = (position: Position) => {
    setPosition(position);
    localStorage.setItem('developer-tools-position', position);
  };

  useEffect(() => {
    const position = localStorage.getItem('developer-tools-position');
    if (position) {
      setPosition(position as Position);
    }
  }, []);

  const getPositionClasses = (position: Position) => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-center':
        return 'bottom-4 left-1/2 -translate-x-1/2';
      case 'top-center':
      default:
        return 'top-4 left-1/2 -translate-x-1/2';
    }
  };

  if (!(isDev && developerTools && isCobler && !isTest) || DISABLED_DEV_TOOLS) return null;

  const sideButtons = [
    {
      icon: <Icon name='wrench' size='sm' color='gray-dark' />,
      label: 'Developer Tools',
      action: () => setShowDeveloperToolsDialog(true),
    },
    {
      icon: <img src={prismicSvg} className='size-4 shrink-0' />,
      label: 'Prismic',
      action: () => setShowPrismicToolbar(!showPrismicToolbar),
    },
    {
      icon: <img src={tailwindSvg} className='size-4 shrink-0' />,
      label: 'Tailwind',
      action: () => setShowTailwindIndicator(!showTailwindIndicator),
    },
    {
      icon: <img src={storybookSvg} className='size-4 shrink-0' />,
      label: 'Storybook',
      action: () => window.open('https://master--669e3d3d87a94caa8f567a9b.chromatic.com/', '_blank'),
    },
  ];

  return (
    <>
      <Suspense>
        <ReactQueryDevtoolsProduction />
      </Suspense>
      <div className={`fixed z-[9999] w-fit ${getPositionClasses(position)}`}>
        <motion.div
          className='flex items-center overflow-hidden rounded-lg border border-gray-11 bg-white'
          layout
          transition={{
            type: 'spring',
            damping: 20,
            stiffness: 300,
          }}
        >
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className='relative z-10 flex items-center justify-center bg-gray-12 px-3 py-2 text-center transition-colors duration-200 hover:bg-gray-a12'
            layout
            whileHover={{ scale: 1.05 }}
          >
            <Icon name='settings-2' size='sm' color='white' />
          </motion.button>
          <AnimatePresence mode='wait'>
            {isOpen && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 'auto', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{
                  type: 'spring',
                  damping: 25,
                  stiffness: 300,
                  duration: 0.2,
                }}
                className='flex overflow-hidden'
              >
                {sideButtons.map(({ icon, label, action }, index) => (
                  <Tooltip key={label} content={label}>
                    <motion.button
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{
                        delay: index * 0.1,
                        type: 'spring',
                        damping: 20,
                        stiffness: 300,
                      }}
                      onClick={action}
                      className='flex items-center gap-2 whitespace-nowrap p-2 text-gray-9 transition-colors duration-150 hover:bg-gray-3'
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {icon}
                    </motion.button>
                  </Tooltip>
                ))}
                <PositionData position={position} onPositionChange={handleSetPosition} />
              </motion.div>
            )}
          </AnimatePresence>

          {!isOpen && (
            <>
              <BorderBeam duration={4} size={30} />
              <BorderBeam duration={4} size={30} delay={2} />
            </>
          )}
        </motion.div>

        <AnimatePresence>
          {showTailwindIndicator && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className='absolute top-0.5 left-full ml-2'
            >
              <TailwindIndicator open={showTailwindIndicator} onOpenChange={setShowTailwindIndicator} />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showPrismicToolbar && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className='absolute left-full ml-2'
            >
              <PrismicToolbar />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <DeveloperToolsDialog open={showDeveloperToolsDialog} onClose={() => setShowDeveloperToolsDialog(false)} />
    </>
  );
}

export default DeveloperTools;
